import type { Document, DocumentVersion } from "@models";
import type { TemplateConfig } from "@types";
import {
  findDocInstancesFromTemplate,
  getDocConfigByElementId,
} from "src/utils/documents";
import type {
  DynamicNodeOption,
  DynamicNodeOwner,
} from "./dynamic-node-context";

const getDocumentOwner = (
  key: string,
  documents: Document[],
  currentDocument: Document | null,
  viewedVersion: DocumentVersion | null
): DynamicNodeOwner | null => {
  if (
    currentDocument &&
    viewedVersion?.dataKeys.some((dataKey) => dataKey.dataKeyKey === key)
  ) {
    return {
      title: currentDocument.title,
      documentId: currentDocument.id,
      deviceId: currentDocument.deviceId ?? null,
    };
  }

  let newestOwner: { document: Document; versionCreatedAt: number } | undefined;

  for (const document of documents) {
    for (const version of document.versions) {
      if (!version.dataKeys.some((dataKey) => dataKey.dataKeyKey === key)) {
        continue;
      }

      const versionCreatedAt = version.createdAt.getTime();
      if (!newestOwner || versionCreatedAt > newestOwner.versionCreatedAt) {
        newestOwner = { document, versionCreatedAt };
      }
    }
  }

  return newestOwner
    ? {
        title: newestOwner.document.title,
        documentId: newestOwner.document.id,
        deviceId: newestOwner.document.deviceId ?? null,
      }
    : null;
};

const getExpectedOwner = (
  key: string,
  documents: Document[],
  templatesConfig: TemplateConfig
): DynamicNodeOwner | null => {
  const template = (() => {
    try {
      return getDocConfigByElementId(key, templatesConfig);
    } catch {
      return null;
    }
  })();

  if (!template) {
    return null;
  }

  const existingDocument = findDocInstancesFromTemplate(
    documents,
    template.templateKey
  ).at(0);

  return existingDocument
    ? {
        title: existingDocument.title,
        documentId: existingDocument.id,
        deviceId: existingDocument.deviceId ?? null,
      }
    : {
        title: template.defaultTitle,
        documentId: null,
        deviceId: null,
      };
};

export const addDataKeyOwners = ({
  items,
  documents,
  templatesConfig,
  currentDocument = null,
  viewedVersion = null,
}: {
  items: DynamicNodeOption[];
  documents: Document[];
  templatesConfig: TemplateConfig;
  currentDocument?: Document | null;
  viewedVersion?: DocumentVersion | null;
}): DynamicNodeOption[] =>
  items.map((item) => {
    const owner =
      getDocumentOwner(item.key, documents, currentDocument, viewedVersion) ??
      getExpectedOwner(item.key, documents, templatesConfig);

    return owner ? { ...item, owner } : item;
  });
